import { getDaysFromToday } from '../date'

/**
 * Supported membership types for Xendit invoices
 */
export type MembershipType = 'NEW' | 'ADD-ON' | 'RENEWAL'

/**
 * Supported organization types
 */
export type OrganizationType = 'family' | 'school'

/**
 * Base properties for all invoice descriptions
 */
interface BaseInvoiceProps {
  orgName: string
  orgId: string
  invoiceNumber: string
  invoiceId: string
  type: OrganizationType
  noOfSeats: number
}

/**
 * Props for NEW membership invoice
 */
export interface XenditInvoiceDescriptionNew extends BaseInvoiceProps {
  invoiceType: 'NEW'
  premiumDuration: number | string
}

/**
 * Props for ADD-ON membership invoice
 */
export interface XenditInvoiceDescriptionAddOn extends BaseInvoiceProps {
  invoiceType: 'ADD-ON'
  endDate: Date
}

/**
 * Props for RENEWAL membership invoice
 */
export interface XenditInvoiceDescriptionRenewal extends BaseInvoiceProps {
  invoiceType: 'RENEWAL'
  premiumDuration: number | string
  /**
   * Of `noOfSeats`, how many are EMPTY seats that start at payment rather than at the handover
   * and are charged pro-rata for the days in between. Named on the gateway page so the amount
   * does not read as a plain `rate x seats x months` that it no longer is.
   */
  bridgedSeats?: number
}

export type XenditInvoiceDescriptionProps =
  | XenditInvoiceDescriptionNew
  | XenditInvoiceDescriptionAddOn
  | XenditInvoiceDescriptionRenewal

/**
 * Result of invoice description generation
 */
export interface XenditInvoiceDescriptionResult {
  description: string
  itemName: string
  referenceId: string
}

/**
 * Generates a properly formatted description, item name, and reference ID for Xendit invoices
 * based on the invoice type and organization type.
 *
 * @param props - Invoice properties including type, organization info, and membership details
 * @returns Object containing description, itemName, and referenceId
 *
 * @example
 * ```typescript
 * const result = xenditInvoiceDescription({
 *   invoiceType: 'NEW',
 *   type: 'family',
 *   orgName: 'Keluarga Budi',
 *   orgId: 'org-123',
 *   invoiceNumber: 'INV-001',
 *   invoiceId: 'inv-123',
 *   noOfSeats: 5,
 *   premiumDuration: 12
 * })
 * ```
 */
export const xenditInvoiceDescription = (props: XenditInvoiceDescriptionProps): XenditInvoiceDescriptionResult => {
  const { invoiceType, type, orgName, orgId, invoiceId, invoiceNumber, noOfSeats } = props

  // Helper function to pluralize "kursi" (seats)
  const formatSeats = (count: number) => `${count} kursi`

  // Generate reference ID
  const generateReferenceId = () => {
    const typeUpper = type.toUpperCase()
    return `${invoiceType}/${typeUpper}/${orgId}/${invoiceId}`
  }

  // Handle NEW membership type
  if (invoiceType === 'NEW') {
    const duration = props.premiumDuration
    const seats = formatSeats(noOfSeats)

    if (type === 'family') {
      return {
        description: `Pembelian Layanan PiBo Premium ${duration} bulan untuk ${orgName} sebanyak ${seats}. ${invoiceNumber}.`,
        itemName: `Layanan PiBo Premium ${duration} Bulan untuk ${seats}`,
        referenceId: generateReferenceId(),
      }
    }

    return {
      description: `Pembayaran Sekolah Premium ${duration} bulan untuk ${orgName} sebanyak ${seats}. ${invoiceNumber}.`,
      itemName: `Sekolah Premium ${duration} Bulan untuk ${seats}`,
      referenceId: generateReferenceId(),
    }
  }

  // Handle RENEWAL membership type
  if (invoiceType === 'RENEWAL') {
    const duration = props.premiumDuration
    const seats = formatSeats(noOfSeats)
    const bridgeNote =
      props.bridgedSeats && props.bridgedSeats > 0
        ? ` Termasuk pro-rata ${formatSeats(props.bridgedSeats)} kosong yang aktif sejak pembayaran.`
        : ''

    if (type === 'family') {
      return {
        description: `Pembelian Perpanjangan Layanan Premium ${duration} bulan untuk ${orgName} sebanyak ${seats}.${bridgeNote} ${invoiceNumber}.`,
        itemName: `Layanan PiBo Premium ${duration} Bulan untuk ${seats}`,
        referenceId: generateReferenceId(),
      }
    }

    return {
      description: `Pembayaran Perpanjangan Sekolah Premium ${duration} bulan untuk ${orgName} sebanyak ${seats}.${bridgeNote} ${invoiceNumber}.`,
      itemName: `Sekolah Premium ${duration} Bulan untuk ${seats}`,
      referenceId: generateReferenceId(),
    }
  }

  // Handle ADD-ON membership type
  const days = getDaysFromToday(props.endDate).daysDifference
  const seats = formatSeats(noOfSeats)

  return {
    description: `Pembayaran Kursi Tambahan ${days} hari untuk ${orgName} sebanyak ${seats}. ${invoiceNumber}.`,
    itemName: `Kursi Tambahan ${days} hari untuk ${seats}`,
    referenceId: generateReferenceId(),
  }
}
